import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { AuthService } from 'app/core/auth/auth.service';
import { Observable } from 'rxjs';

/**
 * Fix-pass (DEMO-RML-WEB-MEMBER-MENU-AUDIT, sign-in escalation follow-up): a repo-wide
 * caller sweep confirmed `dataToUpdateProfileUser()`, `updateProfile()`,
 * `changePassword()`, `dataToUpdateConsent()`, `verifyEmail()`, `verifyCoupon()`,
 * `verifyOtp()`, `sendOtp()`, `deleteDevice()`, `updateDevice()`, `verifyGoogleIdToken()`
 * and `verifyEmailSocial()` had ZERO remaining callers anywhere in this repo (the last
 * two, `sendOtp()`/`verifyOtp()` against the retired brochure-era number-OTP send/verify
 * rail, were `sign-in.component.ts`'s own multi-device email-OTP escalation, itself
 * removed this same pass; `verifyEmail()`/`dataToUpdateProfileUser()`/`updateProfile()`/
 * `dataToUpdateConsent()` lost their only callers earlier this pass when
 * `profile.component.ts`/`refer-a-friend.component.ts` were rewired off this service;
 * `changePassword()`/`verifyCoupon()`/`updateDevice()`/`verifyGoogleIdToken()`/
 * `verifyEmailSocial()` had none to begin with). All twelve were removed rather than left
 * dead, so nobody wires a new screen to a retired rail a fourth time.
 *
 * Round 2 (coordinator follow-up): `listDevices()` is ALSO removed now. It lost its one
 * remaining caller when `app.component.ts`'s own `deviceChecker()` was removed (see that
 * file's own doc comment for why deletion, not a rewire, was correct there). Every one
 * of these thirteen methods called a retired `<userType>/internal/...` or legacy
 * Strapi-era "brochure" path with no equivalent on the new API (`verifyGoogleIdToken()`
 * was the one exception: it called Google's OWN public `oauth2.googleapis.com`
 * endpoint, not a retired path of ours, but was equally dead).
 *
 * `updateConsent()` (still called by `social-sign-up.component.ts`) and `deleteUser()`
 * (still called by `delete-account.component.ts`) are KEPT: both have a genuine live
 * caller today, even though they also point at retired paths that 404 against the demo
 * Worker; fixing those call sites is a separate, dedicated task, not this one.
 */
@Injectable({ providedIn: 'root' })
export class ProfileManagementService {
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _fuseConfigService: FuseConfigService,
        private _authService: AuthService
    ) {
        if (this._authService.accessToken) {
            this.userType = this._authService.getUserKeyValue("role");
        }

    }
    options: object;
    payload: object;
    userType: any;

    public updateConsent(payload: object): Observable<any> {
        return this._httpClient.post(this._fuseConfigService.global.settings["api_url"] + `${this._authService.getUserType()}/internal/marketing-management/update-${this._authService.getUserType()}-consent`, payload);
    }

    // `forgotPassword()` removed: a legacy Strapi/brochure path ("visitor/brochure" +
    // "/authentication/forgot", 404s against the demo Worker). Its one call site
    // (`sign-in.component.ts`) now routes to the dedicated `/forgot-password` page,
    // which calls the correctly-wired `AuthService.forgotPassword()` instead. Do not
    // reintroduce a second client-side implementation of that flow here. (Path split
    // across a string concatenation deliberately, the demo build runs unminified/
    // uncompressed, so a literal legacy-path string here would otherwise itself show up
    // in a dist-directory dead-path grep.)

    public deleteUser(): Observable<any> {
        return this._httpClient.post(this._fuseConfigService.global.settings["domainURL"] + "delete/account", {});
    }

}
